import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { Plus, Trash2, GripVertical, Save } from 'lucide-react';
import { CustomField, CreateCustomFieldDto, CustomFieldType, EntityType } from '../../types/admin';

interface CustomFieldBuilderProps {
  fields: CustomField[];
  entityType: EntityType;
  categoryId?: string;
  onSave: (data: CreateCustomFieldDto) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const FIELD_TYPES: { value: CustomFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'radio', label: 'Radio' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Date' },
  { value: 'textarea', label: 'Text Area' },
];

export const CustomFieldBuilder: React.FC<CustomFieldBuilderProps> = ({
  fields, entityType, categoryId, onSave, onDelete,
}) => {
  const [newField, setNewField] = useState<Partial<CreateCustomFieldDto>>({
    field_name: '', field_type: 'text', is_required: false, options: [], sort_order: fields.length + 1,
  });
  const [optionInput, setOptionInput] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const filteredFields = fields.filter(f => f.entity_type === entityType && (categoryId ? f.category_id === categoryId : true));

  const handleAddOption = () => {
    if (optionInput.trim()) {
      setNewField(prev => ({ ...prev, options: [...(prev.options || []), optionInput.trim()] }));
      setOptionInput('');
    }
  };

  const handleRemoveOption = (idx: number) => {
    setNewField(prev => ({ ...prev, options: (prev.options || []).filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    if (!newField.field_name) return;
    await onSave({
      entity_type: entityType,
      category_id: categoryId,
      field_name: newField.field_name || '',
      field_type: newField.field_type || 'text',
      is_required: newField.is_required || false,
      options: newField.options,
      sort_order: newField.sort_order || fields.length + 1,
    });
    setNewField({ field_name: '', field_type: 'text', is_required: false, options: [], sort_order: filteredFields.length + 2 });
    setIsAdding(false);
  };

  return (
    <div className="space-y-3">
      {filteredFields.length === 0 && !isAdding && (
        <p className="text-xs text-slate-500 text-center py-4">No custom fields configured for this entity.</p>
      )}

      {filteredFields.map(field => (
        <div key={field.field_id} className="flex items-center gap-3 p-3 bg-slate-900/60 border border-slate-800 rounded-lg">
          <GripVertical className="w-4 h-4 text-slate-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-200 truncate">{field.field_name}</span>
              <Badge label={field.field_type} variant="info" />
              {field.is_required && <Badge label="Required" variant="warning" />}
            </div>
            {field.options && field.options.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {field.options.map((opt, i) => (
                  <span key={i} className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded">{opt}</span>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => onDelete(field.field_id)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors shrink-0">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}

      {isAdding ? (
        <div className="p-4 bg-slate-900/60 border border-sky-500/30 rounded-xl space-y-3">
          <Input
            value={newField.field_name || ''}
            onChange={e => setNewField(prev => ({ ...prev, field_name: e.target.value }))}
            placeholder="Field name (e.g., AC Make/Brand)"
          />
          <div className="grid grid-cols-2 gap-3">
            <select
              value={newField.field_type}
              onChange={e => setNewField(prev => ({ ...prev, field_type: e.target.value as CustomFieldType }))}
              className="bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-xs text-slate-100"
            >
              {FIELD_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
            </select>
            <label className="flex items-center gap-2 text-xs text-slate-400">
              <input
                type="checkbox"
                checked={newField.is_required || false}
                onChange={e => setNewField(prev => ({ ...prev, is_required: e.target.checked }))}
                className="rounded border-slate-600"
              />
              Required
            </label>
          </div>

          {(newField.field_type === 'dropdown' || newField.field_type === 'radio') && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input value={optionInput} onChange={e => setOptionInput(e.target.value)} placeholder="Add option" onKeyDown={e => e.key === 'Enter' && handleAddOption()} />
                <Button variant="outline" size="sm" onClick={handleAddOption}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {(newField.options || []).map((opt, i) => (
                  <span key={i} className="text-[10px] bg-sky-500/10 text-sky-400 px-2 py-0.5 rounded flex items-center gap-1">
                    {opt}
                    <button onClick={() => handleRemoveOption(i)} className="hover:text-rose-400">&times;</button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" size="sm" onClick={() => setIsAdding(false)}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleSave} icon={<Save className="w-3.5 h-3.5" />}>Save Field</Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setIsAdding(true)} icon={<Plus className="w-3.5 h-3.5" />}>
          Add New Field
        </Button>
      )}
    </div>
  );
};
